import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlarmClockPlus,
  Anchor,
  AppWindow,
  Archive,
  ArrowDown,
  ArrowDownToLine,
  ArrowDownUp,
  ArrowDownWideNarrow,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightFromLine,
  ArrowRightToLine,
  ArrowUp,
  ArrowUpFromLine,
  AudioLines,
  Award,
  BadgeCheck,
  Ban,
  Banknote,
  BarChart3,
  BatteryFull,
  BatteryWarning,
  Bell,
  BookOpen,
  Bot,
  Braces,
  Brain,
  BrainCircuit,
  Bug,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChartArea,
  ChartLine,
  ChartNoAxesCombined,
  ChartPie,
  ChartScatter,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsDownUp,
  ChevronsUpDown,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleCheckBig,
  CircleDollarSign,
  CircleDot,
  CircleHelp,
  CirclePause,
  CirclePlay,
  CirclePlus,
  CircleStop,
  CircleUserRound,
  CircleX,
  ClipboardCheck,
  ClipboardClock,
  ClipboardList,
  Clock,
  ClockArrowUp,
  Cloud,
  CloudDownload,
  CloudOff,
  CloudSync,
  CloudUpload,
  Code,
  Coins,
  Columns2,
  Columns3,
  Compass,
  Construction,
  Contrast,
  Cookie,
  Copy,
  CornerDownRight,
  Database,
  DatabaseBackup,
  DollarSign,
  Download,
  Ear,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  FileDown,
  FileOutput,
  FileSearch,
  FileText,
  FileUp,
  Film,
  Filter,
  Fingerprint,
  Flag,
  FlaskConical,
  Folder,
  FolderArchive,
  FolderOpen,
  FolderX,
  GalleryVerticalEnd,
  Gauge,
  Gift,
  Globe,
  GlobeLock,
  Grid2x2,
  Hammer,
  HardDriveDownload,
  Hash,
  Heart,
  HeartPulse,
  Hexagon,
  History,
  Hourglass,
  House,
  Image,
  ImageUpscale,
  Inbox,
  Info,
  Key,
  KeyRound,
  Landmark,
  Languages,
  Layers,
  LayoutGrid,
  LibraryBig,
  LifeBuoy,
  Lightbulb,
  Link,
  List,
  ListChecks,
  ListChevronsDownUp,
  ListChevronsUpDown,
  ListFilter,
  ListX,
  LoaderCircle,
  LocateFixed,
  Lock,
  LockKeyhole,
  LockOpen,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Medal,
  Megaphone,
  MemoryStick,
  Menu,
  MessageSquare,
  MessagesSquare,
  Mic,
  Microscope,
  Minimize2,
  Minus,
  MonitorDown,
  Moon,
  MoreHorizontal,
  MoreVertical,
  MoveHorizontal,
  MoveRight,
  Music,
  Network,
  OctagonAlert,
  Orbit,
  Package,
  Palette,
  PanelLeft,
  PanelLeftClose,
  PanelLeftOpen,
  Pause,
  Pencil,
  Percent,
  PieChart,
  Pin,
  Play,
  Plug,
  PlugZap,
  Plus,
  Pointer,
  Power,
  Puzzle,
  Radar,
  Receipt,
  ReceiptText,
  RectangleEllipsis,
  RefreshCw,
  RefreshCwOff,
  Reply,
  Rocket,
  RotateCcw,
  Route,
  RouteOff,
  Rows2,
  Rows3,
  Save,
  Scale,
  Search,
  SearchX,
  Send,
  Server,
  Settings,
  Share2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldUser,
  ShieldX,
  Shuffle,
  Signal,
  SlidersHorizontal,
  Sparkles,
  Speech,
  Split,
  Square,
  SquareDashed,
  Star,
  Store,
  Sun,
  Tag,
  Terminal,
  Timer,
  ToggleLeft,
  ToggleRight,
  Trash,
  Trash2,
  TrendingDown,
  TrendingUp,
  Trophy,
  Unlink,
  Upload,
  User,
  UserCog,
  UserKey,
  Users,
  Video,
  Volume2,
  Wallet,
  Wand2,
  Waves,
  Waypoints,
  Webhook,
  Wifi,
  WifiOff,
  Workflow,
  Wrench,
  X,
  Zap,
  // lote 3 (KEA-7886)
  Brackets,
  Cable,
  CalendarX,
  Gavel,
  GlobeOff,
  Hand,
  Monitor,
  MonitorSmartphone,
  Paperclip,
  ShieldOff,
  SquareFunction,
  SquarePen,
  Type,
} from "lucide-react";
// Combos editor + routing strategies (KEA-7886, lote 4).
import {
  ArrowUpToLine,
  BatteryCharging,
  CalendarSync,
  CircleDashed,
  Dices,
  GitCommitHorizontal,
  GripVertical,
  HandHeart,
  IdCard,
  ListEnd,
  ListPlus,
  SkipForward,
  Tally1,
  Tally2,
  Tally3,
  Target,
} from "lucide-react";
// Cache, runtime, chaos, profile, conversations, webhooks (KEA-7886, lote 8).
// ArrowDownToLine, Gift, Heart, LayoutGrid and Medal are imported by the base blocks above.
import {
  CircleMinus,
  Droplet,
  Flame,
  Infinity as InfinityIcon,
  Merge,
  Rss,
  Shapes,
  Snowflake,
  Swords,
} from "lucide-react";
// Lote 9 — tools, system, resilience, rankings, changelog, provider-stats (KEA-7886).
import {
  BadgePlus,
  BellOff,
  BrushCleaning,
  NotebookPen,
  PartyPopper,
  Radio,
  SquareCode,
  Stethoscope,
} from "lucide-react";
// F4 sweep (KEA-7886): the last null entries + lookup-object values the literal scanner missed.
// Droplet and Mail are imported by the blocks above.
import {
  Aperture,
  AudioWaveform,
  BellRing,
  Bird,
  Box,
  BrainCog,
  BriefcaseBusiness,
  Brush,
  CircuitBoard,
  CloudCheck,
  Factory,
  Fence,
  Flower2,
  Gem,
  Handshake,
  LayoutDashboard,
  Leaf,
  MicVocal,
  PackageCheck,
  PawPrint,
  Plane,
  Router,
  ScanText,
  ShieldPlus,
  Smile,
  Table2,
  UserLock,
  UsersRound,
  Wand,
  Wind,
} from "lucide-react";

// F4 (Task 41) — picks that the F4 sweep block above does not import (KEA-7886).
import { ArrowBigUpDash, Blend } from "lucide-react";

/**
 * Icon name (the Material Symbols vocabulary the screens use) → Lucide icon. Every name in use
 * MUST map to a Lucide glyph (tests/unit/icon-map-coverage.test.ts for literal names,
 * tests/unit/icon-map-object-values.test.ts for object-map values); `npm run gen:icon-map`
 * prints the missing ones. An unmapped name renders <Icon>'s neutral placeholder glyph.
 * Navigation uses one ink (no per-item accent) — SIDEBAR_ICON_ACCENTS was retired (spec §3.5).
 */
export const ICON_MAP: Record<string, LucideIcon> = {
  // ── shell + primitives ──
  anchor: Anchor,
  home: House,
  settings: Settings,
  search: Search,
  close: X,
  menu: Menu,
  logout: LogOut,
  check: Check,
  expand_more: ChevronDown,
  expand_less: ChevronUp,
  chevron_right: ChevronRight,
  chevron_left: ChevronLeft,
  push_pin: Pin,
  left_panel_open: PanelLeftOpen,
  // lote 3 (onboarding STEP_ICONS — an array literal the generator does not scan)
  waving_hand: Hand,
  left_panel_close: PanelLeftClose,
  view_sidebar: PanelLeft,
  light_mode: Sun,
  dark_mode: Moon,
  restart_alt: RotateCcw,
  power_settings_new: Power,
  power_off: Power,
  progress_activity: LoaderCircle,
  info: Info,
  help: CircleHelp,
  visibility: Eye,
  visibility_off: EyeOff,
  error: CircleAlert,
  warning: CircleAlert,
  check_circle: CircleCheck,
  cancel: CircleX,
  inbox: Inbox,
  add: Plus,
  delete: Trash2,
  edit: Pencil,
  content_copy: Copy,
  refresh: RefreshCw,
  cached: RefreshCw,
  open_in_new: ExternalLink,
  more_vert: MoreVertical,
  more_horiz: MoreHorizontal,
  arrow_back: ArrowLeft,
  arrow_forward: ArrowRight,
  arrow_upward: ArrowUp,
  arrow_downward: ArrowDown,
  download: Download,
  upload: Upload,
  save: Save,
  star: Star,
  notifications: Bell,
  schedule: Clock,
  lock: Lock,
  lock_open: LockOpen,
  play_arrow: Play,
  pause: Pause,
  stop: Square,
  healing: HeartPulse,
  code: Code,
  data_object: Braces,
  mail: Mail,
  person: User,
  group: Users,
  groups: Users,
  language: Globe,
  public: Globe,
  calendar_today: Calendar,
  block: Ban,
  database: Database,
  storage: Database,
  // ── sidebar sections (src/shared/constants/sidebarVisibility/sections.ts) ──
  account_balance_wallet: Wallet,
  account_tree: Network,
  admin_panel_settings: ShieldCheck,
  analytics: BarChart3,
  api: Plug,
  archive: Archive,
  auto_awesome: Sparkles,
  auto_fix_high: Wand2,
  bar_chart: BarChart3,
  blender: Shuffle,
  bolt: Zap,
  bug_report: Bug,
  campaign: Megaphone,
  cloud: Cloud,
  cloud_upload: CloudUpload,
  compress: Minimize2,
  deployed_code: Package,
  description: FileText,
  device_hub: Network,
  dns: Server,
  emoji_events: Trophy,
  engineering: Wrench,
  extension: Puzzle,
  filter_alt: Filter,
  flag: Flag,
  folder: Folder,
  forum: MessagesSquare,
  grain: Grid2x2,
  health_and_safety: HeartPulse,
  hub: Orbit,
  image_search: Image,
  key: Key,
  labs: FlaskConical,
  lan: Network,
  layers: Layers,
  leaderboard: Medal,
  link: Link,
  manage_search: Search,
  memory: MemoryStick,
  menu_book: BookOpen,
  monitor_heart: Activity,
  monitoring: Gauge,
  network_check: Signal,
  palette: Palette,
  perm_media: Film,
  pie_chart: PieChart,
  policy: Scale,
  price_change: DollarSign,
  psychology: Brain,
  radar: Radar,
  request_quote: Receipt,
  robot: Bot,
  route: Route,
  route_off: RouteOff,
  savings: Coins,
  science: Microscope,
  security: Shield,
  share: Share2,
  shield: ShieldCheck,
  smart_toy: Bot,
  speed: Gauge,
  table_rows: Rows3,
  terminal: Terminal,
  timeline: History,
  timer_off: Timer,
  toll: Coins,
  translate: Languages,
  travel_explore: Compass,
  tune: SlidersHorizontal,
  view_column: Columns3,
  // combos (lote 4): the "Quality First" mode pack names its icon in data (intelligentRouting.ts)
  target: Target,
  view_list: List,
  view_timeline: CalendarClock,
  vpn_key: KeyRound,
  webhook: Webhook,
  // ── everything else in use — generator output (`npm run gen:icon-map`), not a hand-curated list:
  // names the scanner finds in icon slots that still render through the Material fallback.
  // null until the owning screen's lote maps it; the generator validates every name against
  // the Material Symbols vocabulary, so nothing here is a non-icon string. ──
  ac_unit: Snowflake,
  account_balance: Landmark,
  account_circle: CircleUserRound,
  add_circle: CirclePlus,
  add_task: ListPlus,
  air: Wind,
  alarm_add: AlarmClockPlus,
  all_inclusive: InfinityIcon,
  alt_route: Split,
  article: FileText,
  assignment: ClipboardList,
  attach_file: Paperclip,
  attach_money: DollarSign,
  auto_delete: Trash,
  auto_fix_normal: Wand,
  autorenew: RefreshCw,
  backup: DatabaseBackup,
  badge: IdCard,
  balance: Scale,
  batch_prediction: Layers,
  battery_alert: BatteryWarning,
  battery_charging_full: BatteryCharging,
  battery_status_good: BatteryFull,
  blur_on: Aperture,
  brush: Brush,
  build: Wrench,
  build_circle: Wrench,
  business: Building2,
  business_center: BriefcaseBusiness,
  cable: Cable,
  call_split: Split,
  casino: Dices,
  category: Shapes,
  celebration: PartyPopper,
  chat: MessageSquare,
  chat_bubble: MessageSquare,
  checklist: ListChecks,
  cleaning_services: BrushCleaning,
  cloud_done: CloudCheck,
  cloud_off: CloudOff,
  cloud_queue: Cloud,
  cloud_sync: CloudSync,
  code_blocks: SquareCode,
  collapse_all: ListChevronsDownUp,
  compare: Columns2,
  compare_arrows: ArrowLeftRight,
  computer: Monitor,
  construction: Construction,
  contrast: Contrast,
  cookie: Cookie,
  dashboard: LayoutDashboard,
  data_array: Brackets,
  data_usage: ChartPie,
  date_range: CalendarRange,
  delete_forever: Trash2,
  delete_sweep: ListX,
  deployed_code_history: PackageCheck,
  developer_board: CircuitBoard,
  devices: MonitorSmartphone,
  diamond: Gem,
  diversity_3: UsersRound,
  do_not_disturb: CircleMinus,
  do_not_disturb_on: CircleMinus,
  document_scanner: ScanText,
  done_all: CheckCheck,
  download_done: HardDriveDownload,
  downloading: CloudDownload,
  drag_indicator: GripVertical,
  dynamic_feed: GalleryVerticalEnd,
  eco: Leaf,
  edit_note: NotebookPen,
  edit_square: SquarePen,
  electrical_services: PlugZap,
  email: Mail,
  error_outline: CircleAlert,
  event_available: CalendarCheck,
  event_busy: CalendarX,
  event_repeat: CalendarSync,
  expand_all: ListChevronsUpDown,
  explore: Compass,
  face: Smile,
  fact_check: ClipboardCheck,
  favorite: Heart,
  fiber_manual_record: CircleDot,
  file_download: FileDown,
  file_export: FileOutput,
  file_upload: FileUp,
  filter_list: ListFilter,
  find_in_page: FileSearch,
  fingerprint: Fingerprint,
  flight: Plane,
  flutter_dash: Bird,
  folder_off: FolderX,
  folder_open: FolderOpen,
  folder_zip: FolderArchive,
  function: SquareFunction,
  gate: Fence,
  gavel: Gavel,
  generating_tokens: Sparkles,
  gpp_bad: ShieldX,
  gpp_maybe: ShieldAlert,
  graphic_eq: AudioLines,
  grid_view: LayoutGrid,
  group_work: Blend,
  handshake: Handshake,
  handyman: Hammer,
  hearing: Ear,
  history: History,
  hourglass_empty: Hourglass,
  hourglass_top: Hourglass,
  image: Image,
  input: ArrowRightToLine,
  insert_drive_file: File,
  install_desktop: MonitorDown,
  inventory_2: Archive,
  // Lucide has no key-off glyph; KeyRound is the closest key icon (the "no key" meaning comes from the label).
  key_off: KeyRound,
  key_vertical: KeyRound,
  keyboard_arrow_down: ChevronDown,
  keyboard_arrow_up: ChevronUp,
  keyboard_capslock: ArrowBigUpDash,
  label: Tag,
  library_books: LibraryBig,
  linear_scale: GitCommitHorizontal,
  link_off: Unlink,
  list: List,
  local_fire_department: Flame,
  local_florist: Flower2,
  location_on: MapPin,
  lock_clock: LockKeyhole,
  lock_person: UserLock,
  login: LogIn,
  looks_3: Tally3,
  looks_one: Tally1,
  looks_two: Tally2,
  low_priority: ListEnd,
  manage_accounts: UserCog,
  manage_history: ClockArrowUp,
  merge: Merge,
  mic: Mic,
  military_tech: Medal,
  minimize: Minus,
  model_training: BrainCircuit,
  move_up: ArrowUpFromLine,
  movie: Film,
  music_note: Music,
  my_location: LocateFixed,
  network_node: Waypoints,
  network_ping: Radar,
  neurology: BrainCircuit,
  new_releases: BadgePlus,
  notifications_active: BellRing,
  notifications_off: BellOff,
  open_in_browser: ExternalLink,
  output: ArrowRightFromLine,
  paid: CircleDollarSign,
  passkey: UserKey,
  pause_circle: CirclePause,
  payments: Banknote,
  pending: CircleDashed,
  pending_actions: ClipboardClock,
  percent: Percent,
  pets: PawPrint,
  photo_size_select_large: ImageUpscale,
  pin: RectangleEllipsis,
  play_circle: CirclePlay,
  playlist_add: ListPlus,
  precision_manufacturing: Factory,
  priority_high: CircleAlert,
  psychology_alt: BrainCog,
  public_off: GlobeOff,
  query_stats: ChartNoAxesCombined,
  radio_button_checked: CircleDot,
  radio_button_unchecked: Circle,
  receipt_long: ReceiptText,
  record_voice_over: Speech,
  redeem: Gift,
  remove: Minus,
  replay: RotateCcw,
  reply: Reply,
  report: OctagonAlert,
  restore: RotateCcw,
  rocket_launch: Rocket,
  router: Router,
  rss_feed: Rss,
  rule: ListChecks,
  scatter_plot: ChartScatter,
  schema: Workflow,
  score: Award,
  search_off: SearchX,
  select_all: SquareDashed,
  sell: Tag,
  send: Send,
  sensors: Radio,
  settings_backup_restore: RotateCcw,
  shield_lock: LockKeyhole,
  shield_person: ShieldUser,
  shield_with_heart: ShieldPlus,
  show_chart: ChartLine,
  shuffle: Shuffle,
  skip_next: SkipForward,
  sort: ArrowDownWideNarrow,
  spatial_audio: AudioWaveform,
  stacked_line_chart: ChartArea,
  stop_circle: CircleStop,
  storefront: Store,
  stream: Waves,
  subdirectory_arrow_right: CornerDownRight,
  support: LifeBuoy,
  swap_horiz: ArrowLeftRight,
  swap_vert: ArrowDownUp,
  swords: Swords,
  sync: RefreshCw,
  sync_alt: ArrowLeftRight,
  sync_disabled: RefreshCwOff,
  sync_problem: RefreshCwOff,
  system_update_alt: MonitorDown,
  table_chart: Table2,
  tag: Hash,
  task_alt: CircleCheckBig,
  text_fields: Type,
  text_snippet: FileText,
  timer: Timer,
  tips_and_updates: Lightbulb,
  today: CalendarDays,
  toggle_off: ToggleLeft,
  toggle_on: ToggleRight,
  token: Hexagon,
  touch_app: Pointer,
  trending_down: TrendingDown,
  trending_flat: MoveRight,
  trending_up: TrendingUp,
  troubleshoot: Stethoscope,
  unfold_less: ChevronsDownUp,
  unfold_more: ChevronsUpDown,
  upload_file: FileUp,
  verified: BadgeCheck,
  verified_user: ShieldCheck,
  vertical_align_bottom: ArrowDownToLine,
  vertical_align_top: ArrowUpToLine,
  videocam: Video,
  view_agenda: Rows2,
  view_in_ar: Box,
  view_module: LayoutGrid,
  voice_chat: MicVocal,
  volume_up: Volume2,
  volunteer_activism: HandHeart,
  vpn_key_off: ShieldOff,
  vpn_lock: GlobeLock,
  water: Droplet,
  water_drop: Droplet,
  web: AppWindow,
  widgets: LayoutGrid,
  width_normal: MoveHorizontal,
  wifi: Wifi,
  wifi_off: WifiOff,
  workspace_premium: Award,
};

export function resolveIcon(name: string | null | undefined): LucideIcon | null {
  // Own keys only: `constructor`/`toString` must not resolve to Object.prototype members.
  if (!name || !Object.hasOwn(ICON_MAP, name)) return null;
  return ICON_MAP[name];
}
